/**
 * Per-diem suppression helpers for the covered charges table.
 *
 * A per-diem program (HealthcareService with isPerDiem === true) declares the
 * set of procedure codes that get rolled up into a per-diem charge. Those
 * procedures should not appear on the covered charges list while the encounter
 * is in-progress — they are routed exclusively to the per-diem report instead.
 *
 * These helpers are pure (no Vue, no API) so they can be unit-tested and
 * reused outside the composable layer.
 */

const PART_OF_WALK_MAX_DEPTH = 10;

/**
 * Resolve the set of per-diem applicable procedure codes for a single program.
 *
 * Gates on `isPerDiem === true` — a non-per-diem program (even one carrying a stray
 * `perDiemProcedure` reference) resolves to an empty Set, so its session codes are never
 * suppressed (#4728). Codes come from either (a) the program's own
 * `perDiem.applicableServices`, or (b) the referenced billable HealthcareService
 * (`program.perDiemProcedure.id`, resolved through `hsById`). In current BN data the
 * convention is (b); (a) is a fallback for data that diverges.
 *
 * Shared by both per-diem suppression surfaces: the covered-charges table
 * (`buildPerDiemSuppressionMap`) and the sign-and-lock charge-creation gate
 * (`useQuestionnaireFlow.findPerDiemApplicableCodesFromEncounters`).
 *
 * @param {object} program  HealthcareService program record
 * @param {Map<string, object>} hsById  HealthcareService records indexed by id
 * @returns {Set<string>} applicable procedure codes (empty when the program is not per-diem)
 */
export function resolvePerDiemApplicableCodes(program, hsById) {
    const codes = new Set();
    if (!program || program.isPerDiem !== true) return codes;

    const direct = program.perDiem?.applicableServices;
    if (Array.isArray(direct)) {
        for (const as of direct) if (as?.code) codes.add(as.code);
    }

    const billableId = program.perDiemProcedure?.id;
    if (billableId && hsById instanceof Map) {
        const referenced = hsById.get(billableId)?.perDiem?.applicableServices;
        if (Array.isArray(referenced)) {
            for (const as of referenced) if (as?.code) codes.add(as.code);
        }
    }

    return codes;
}

/**
 * Build an encounter → applicable-codes suppression map.
 *
 * @param {Array<object>} programs   HealthcareService records (filtered internally to isPerDiem)
 * @param {Array<object>} encounters Encounter records with id, optional partOf.id, optional healthcareService.id
 * @returns {Map<string, Set<string>>} keyed by Encounter.id whose partOf chain resolves to a per-diem program
 */
export function buildPerDiemSuppressionMap(programs, encounters) {
    const result = new Map();
    if (!Array.isArray(programs) || !Array.isArray(encounters)) return result;

    // Index every HealthcareService by id so we can resolve perDiemProcedure references.
    // The `programs` argument is the flat fetch result, which includes both program-level
    // records (isPerDiem: true) and the per-diem billable services they reference via
    // perDiemProcedure (pulled in by _include=HealthcareService:perDiemProcedure).
    const hsById = new Map();
    for (const p of programs) {
        if (p?.id) hsById.set(p.id, p);
    }

    // Build programId → Set<applicableCode> via the shared resolver (gates on isPerDiem,
    // unions the program's own applicableServices with the referenced billable service's).
    const programCodesById = new Map();
    for (const program of programs) {
        if (!program?.id) continue;
        const codes = resolvePerDiemApplicableCodes(program, hsById);
        if (codes.size === 0) continue;
        programCodesById.set(program.id, codes);
    }
    if (!programCodesById.size) return result;

    // Index encounters by id for partOf walking.
    const encounterById = new Map();
    for (const enc of encounters) {
        if (enc?.id) encounterById.set(enc.id, enc);
    }

    /**
     * Walk the partOf chain from `encounter` looking for a per-diem program reference.
     * Returns the program id (key into programCodesById) or null.
     */
    const findPerDiemProgramId = (encounter) => {
        let current = encounter;
        const seen = new Set();
        for (let depth = 0; depth < PART_OF_WALK_MAX_DEPTH; depth++) {
            if (!current || !current.id) return null;
            if (seen.has(current.id)) return null;
            seen.add(current.id);

            const hsId = current.healthcareService?.id;
            if (hsId && programCodesById.has(hsId)) return hsId;

            const parentId = current.partOf?.id;
            if (!parentId) return null;
            current = encounterById.get(parentId);
        }
        return null;
    };

    for (const encounter of encounters) {
        if (!encounter?.id) continue;
        const programId = findPerDiemProgramId(encounter);
        if (!programId) continue;
        result.set(encounter.id, programCodesById.get(programId));
    }

    return result;
}

/**
 * Decide whether a covered-charges row should be suppressed (hidden) because
 * its procedures belong to a per-diem program.
 *
 * @param {object} row
 * @param {string} row.encounterId
 * @param {string|string[]} row.procedureCodes  Either '-' sentinel, a single code, or an array of codes
 * @param {string|null|undefined} row.chargeItemStatus  'planned' | 'billed' | 'not-billable' | null
 * @param {Map<string, Set<string>>} suppressionMap
 * @returns {boolean} true → hide row from covered charges
 */
export function isSuppressedByPerDiem({ encounterId, procedureCodes, chargeItemStatus }, suppressionMap) {
    // Settled rows (already bundled or explicitly bypassed) stay visible.
    if (chargeItemStatus === 'billed' || chargeItemStatus === 'not-billable') return false;
    if (!(suppressionMap instanceof Map)) return false;
    if (!encounterId) return false;

    const codes = suppressionMap.get(encounterId);
    if (!codes || !codes.size) return false;

    // `procedureCodes` may be a single string or the '-' sentinel that
    // transformSqlRowsToItems uses for rows with no procedures. Both are safely
    // handled: '-' is never an applicable code, so it can't match.
    const codeList = Array.isArray(procedureCodes) ? procedureCodes : [procedureCodes];
    for (const code of codeList) {
        if (code && codes.has(code)) return true;
    }
    return false;
}
